<template>
  <Message v-if="showAlert" ref="component" :type="type" class="justify-center">
    <div class="max-w-[788px]">
      <template v-if="subscriptionWillEnd">
        <span class="font-secondary">
          You have canceled your {{ billingStore.billing?.plan.name }} plan and
          it will end on {{ subscriptionRenewalDate }}. You will need to
          <div
            class="inline-block text-yellow-900 underline cursor-pointer font-medium"
            @click="handleResubscribeClick"
          >
            resubscribe
          </div>
          to access your copyrighted work
        </span>
      </template>

      <template v-if="subscriptionCancelled">
        <span class="font-secondary">
          Your account is disabled because you have cancelled your
          {{ billingStore.billing?.plan.name }} plan. Please
          <div
            class="inline-block text-red-900 underline cursor-pointer font-medium"
            @click="handleResubscribeClick"
          >
            resubscribe
          </div>
          to pay your accrued account balance and access your copyrighted work
          again.
        </span>
      </template>

      <template v-if="subscriptionOverdue">
        <span class="font-secondary">
          Your account is disabled because you have an outstanding balance.
          <div>
            Please
            <span
              class="text-red-900 underline cursor-pointer font-medium"
              @click="handlePayClick"
            >
              pay your balance
            </span>
            to continue using Dacr.
          </div>
        </span>
      </template>

      <template v-if="subscriptionRequiresAction">
        <span class="font-secondary">
          Your credit card provider requires you to authenticate your payment.
          Please
          <div
            class="inline-block text-yellow-900 underline cursor-pointer font-medium"
            @click="handleRequiresActionClick"
          >
            click here
          </div>
          to proceed with authentication.
        </span>
      </template>

      <template v-if="subscriptionUpgrade">
        <span class="font-secondary">
          You're at the limit of your plan. Upgrade to Pro to add unlimited
          copyrights
          <RouterLink to="/upgrade" class="inline-flex items-center">
            <div class="text-red-900 font-medium cursor-pointer underline">
              See plans
            </div>
            <Icon :size="13" name="LinkArrow" class="ml-px text-red-900" />
          </RouterLink>
        </span>
      </template>

      <template v-if="subscriptionUpgradeBundle">
        <span class="font-secondary">
          You're at the limit of your plan.
          <div
            class="text-yellow-900 font-medium cursor-pointer underline inline-block"
            @click="handleUpgradeBundleClick"
          >
            Upgrade your copyright limit
          </div>
          to add more copyrights.
        </span>
      </template>

      <template v-if="subscriptionDowngrade">
        <span class="font-secondary">
          {{ billingStore.billing?.billingAlert?.description }}. Your new plan
          will take effect after your current subscription plan ends on
          {{ formatTimestampLocal(billingStore.billing?.plan.dateInfo?.date) }}
        </span>
      </template>

      <template v-if="subscriptionChargeback">
        <span class="font-secondary">
          A payment you previously made was disputed with your bank. Please
          contact your bank to reinstate the payment. Your account will remain
          disabled.
          <div class="text-red-900 font-medium underline inline-block">
            <a
              href="https://help.dacr.com/hc/en-us/requests/new"
              target="_blank"
            >
              Contact us
            </a>
          </div>
          to re-enable your account and settle your balance.
        </span>
      </template>
    </div>
  </Message>
</template>

<script setup lang="ts">
import { useUserStore } from '@/shared/stores/user'
import { getStripe } from '@/shared/services/stripe'
import { formatTimestamp } from '@/shared/utils/datetime'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { usePlatformStore } from '@/shared/stores/platform'
import { useBillingStore } from '@/features/billing/store'
import {
  BillingAlertType,
  BillingQueryDocument,
  PlanStatus,
  type User,
  type BillingQueryQuery,
  UserPermission,
} from '@/shared/graphql/legacy/generated'
import { useWebviewStore } from '@/shared/stores/webview'
import { apolloClient } from '@/shared/graphql/client'
import gql from 'graphql-tag'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { Icon, Message } from '@dacr/ui'
import { useDevice } from '@/shared/composables/useDevice'
import { useRouter } from 'vue-router'

const props = defineProps<{
  root?: boolean
}>()

const device = useDevice()
const router = useRouter()
const dialog = useDialog()
const userStore = useUserStore()
const platformStore = usePlatformStore()
const billingStore = useBillingStore()
const webviewStore = useWebviewStore()
const toastStore = useToastStore()

const user = computed(() => userStore.user)

const subscriptionWillEnd = computed(
  () => billingStore.billing?.billingAlert?.type === BillingAlertType.WillCancel
)
const subscriptionCancelled = computed(
  () => billingStore.billing?.billingAlert?.type === BillingAlertType.Canceled
)
const subscriptionOverdue = computed(
  () => billingStore.billing?.billingAlert?.type === BillingAlertType.Overdue
)
const subscriptionRequiresAction = computed(
  () =>
    billingStore.billing?.billingAlert?.type === BillingAlertType.RequiresAction
)
const subscriptionUpgrade = computed(
  () => billingStore.billing?.billingAlert?.type === BillingAlertType.Upgrade
)
const subscriptionUpgradeBundle = computed(
  () =>
    billingStore.billing?.billingAlert?.type === BillingAlertType.UpgradeBundle
)
const subscriptionDowngrade = computed(
  () => billingStore.billing?.billingAlert?.type === BillingAlertType.Downgrade
)
const subscriptionChargeback = computed(
  () => billingStore.billing?.billingAlert?.type === BillingAlertType.Chargeback
)

const type = computed(() =>
  subscriptionRequiresAction.value ||
  subscriptionUpgradeBundle.value ||
  subscriptionWillEnd.value
    ? 'warning'
    : 'error'
)

const component = ref<InstanceType<typeof Message>>()

const updateBannerHeight = () => {
  if (!props.root) return

  const componentHeight = component.value?.$el.clientHeight || 0

  platformStore.setAlertBannerHeight(componentHeight)
}

watch(component, updateBannerHeight)

const showAlert = computed(() => {
  return billingStore.billing?.billingAlert?.isActive
})

const subscriptionRenewalDate = computed(() =>
  formatTimestamp(billingStore.billing?.billingAlert?.date, {
    formatString: user.value?.dateFormat,
  })
)

const handleResubscribeClick = () => {
  if (!billingStore.canUpdateSubscription) {
    return dialog.open(Dialogs.IAP, {
      props: {
        type: 'change',
      },
    })
  }

  if (webviewStore.isActive) {
    return dialog.open(Dialogs.ResubscribeIAP)
  }

  dialog.open(Dialogs.Resubscribe)
}

const handlePayClick = () => {
  if (!billingStore.canUpdateSubscription && webviewStore.isActive) {
    return dialog.open(Dialogs.CantPay)
  }

  router.push('/settings/billing')

  dialog.open(Dialogs.Pay, {
    events: {
      success() {
        dialog.open(Dialogs.Success, {
          props: {
            title: 'Payment successful',
          },
          events: {
            success() {
              dialog.close()
            },
          },
        })
      },
    },
  })
}

const handleRequiresActionClick = async () => {
  if (webviewStore.isActive) {
    return
  }

  const stripe = await getStripe()

  if (
    !stripe ||
    !billingStore.billing?.paymentMethod?.requiresAction?.clientSecret
  )
    return

  const { error } = await stripe.confirmCardPayment(
    billingStore.billing?.paymentMethod.requiresAction.clientSecret
  )

  if (error) {
    console.log(error)

    toastStore.showToast({
      type: ToastType.Error,
      content: 'Payment authentication failed',
      error: new Error(error.message),
    })
    return
  }

  if (!userStore.user) return

  apolloClient.cache.updateFragment<User>(
    {
      id: apolloClient.cache.identify(userStore.user),
      fragment: gql`
        fragment UpdateUser on User {
          permissions
        }
      `,
    },
    (response) => {
      if (!response) return

      return {
        permissions: userStore.user?.permissions.concat(UserPermission.Pro),
      } as User
    }
  )

  apolloClient.cache.updateQuery<BillingQueryQuery>(
    {
      query: BillingQueryDocument,
    },
    (response) => {
      if (!response) return

      return {
        ...response,
        billing: {
          ...response.billing,
          plan: {
            ...response.billing.plan,
            status: PlanStatus.Active,
            dateInfo: {
              __typename: 'BillingPlanDateInfo',
              date: response.billing.upcomingDetails.upcomingBilling?.date,
              prefix: 'Renews on',
            },
          },
          billingAlert: null,
        },
      } as BillingQueryQuery
    }
  )
}

const handleUpgradeBundleClick = () => {
  const hasReachedLimit =
    (user.value?.totalFiles || 0) >= (user.value?.totalFilesLimit || 0)

  dialog.open(Dialogs.UpgradeBundleLimit, {
    props: {
      hasReachedLimit,
    },
  })
}

const formatTimestampLocal = (timestamp: number) => {
  return formatTimestamp(timestamp, {
    formatString: 'LLLL d, yyyy',
  })
}

onMounted(() => {
  window.addEventListener('resize', updateBannerHeight)
})

onBeforeUnmount(() => {
  if (props.root) {
    platformStore.setAlertBannerHeight(0)
  }
  window.removeEventListener('resize', updateBannerHeight)
})
</script>
